/**
 * The one-line description of each of the six workflows, shared by every
 * surface that lists them as rows: the extension's Workflows tab cards
 * (content/workflows/workflowCards.tsx), its project-view "Run Workflows"
 * dropdown (content/workflowItems.tsx -> WorkflowsDropdown.tsx), and team
 * review's WorkflowMenu (review-app/components/WorkflowMenu.tsx).
 *
 * It lives here because those packages cannot see each other, and because
 * until 2026-09-02 only the tab rendered these sentences: the dropdown took
 * 187 of the 225 compare-row clicks in the prior 25 days and showed label +
 * icon only, so the sentence that tells "Visual Comparison" (a live overlay
 * you read by eye) from "AI Visual Comparison" (a written list of findings)
 * was withheld from the surface carrying 83% of the picks. Six of six inbound
 * help-chat messages asking for "compare" never used the word "AI"
 * (user_decisions_log 2026-09-02).
 *
 * Keys are the card ids (workflowLabels.ts WORKFLOW_CARD_LABELS), in the
 * approved cross-surface order (Emily, 2026-08-08).
 *
 * ONE LINE EACH, 38 characters or fewer (Emily, 2026-09-02: "I want it to be
 * one line and shorter"; every line below is her pick, verbatim). The budget
 * is the narrowest surface, the extension dropdown at 11px, where the
 * committed frames show 38 characters on one line and a 39th word wrapping
 * (docs/test-reports/2026-09-02-workflow-row-descriptions/). The row is a
 * menu label; the panel a row opens keeps its own longer idle paragraph
 * (AutoReviewPanel.tsx, A11yAuditPanel.tsx, TokenAuditPanel.tsx, and team
 * review's extension-only callouts in review-app/App.tsx), so the two are
 * different copy on purpose and no test pins one to the other any more.
 *
 * `packages/shared/src/__tests__/workflowDescriptions.test.ts` pins the budget
 * and fails if a consumer reintroduces an inline copy. No em dashes.
 */
/**
 * The six workflows in the approved cross-surface order (Emily, 2026-08-08): team review's two Workflows menus
 * (review-app/lib/workflowRows.ts) and the free annotation tool's locked menu (guest bundle 0150) read it.
 */
export const WORKFLOW_ROW_IDS = ['auto-review', 'ai-visual-comparison', 'visual-comparison', 'accessibility-audit', 'design-tokens', 'components'];
export const WORKFLOW_CARD_DESCRIPTIONS = {
    'auto-review': 'Spot dead UI, broken states and gaps.',
    'ai-visual-comparison': 'AI-compare and overlay design.',
    'visual-comparison': 'Overlay and adjust design over page.',
    'accessibility-audit': 'Find contrast, label, keyboard issues.',
    'design-tokens': 'Find values that should be tokens.',
    components: 'Find inconsistent, drifted components.',
};
/** Panel explanations are shared too; menu descriptions above stay short.
 * Only name capabilities both interactive surfaces can deliver. Surface-only
 * capabilities (such as the extension's keyboard scanner) remain additive.
 */
export const WORKFLOW_IDLE_DESCRIPTIONS = {
    'accessibility-audit': 'Find WCAG 2.2 AA accessibility issues on this page: contrast, labels and more.',
    'auto-review': 'Scan this page for UI-quality issues a checklist misses: interactive states, forms, responsive gaps and dead UI.',
    'design-tokens': 'Scan this page for hard-coded colors, spacing, type and radius that should be design tokens.',
    components: 'Scan this page for components styled inconsistently: instances of the same element that drift apart.',
};
/**
 * The name of each workflow, keyed like WORKFLOW_CARD_DESCRIPTIONS. It lived in
 * the extension's workflowLabels.ts (which re-exports it, so its importers are
 * unchanged) until 2026-10-25, when team review's row list and the design
 * system's capture-toolbar example needed the same names: the packages cannot
 * see each other, and a third typed copy is how names drift. Flag-INDEPENDENT on
 * purpose: resolving a heading must not depend on gating, or a gate would render
 * "undefined" for a workflow whose flag is not being consulted.
 */
export const WORKFLOW_CARD_LABELS = {
    'visual-comparison': 'Visual Comparison',
    'ai-visual-comparison': 'AI Visual Comparison',
    'auto-review': 'Auto Review',
    'accessibility-audit': 'Accessibility Audit',
    'design-tokens': 'Design Tokens',
    components: 'Components',
};
//# sourceMappingURL=workflowDescriptions.js.map